import * as React from "react";
import { Circle } from "lucide-react";

import { cn } from "./Card";

interface RadioGroupContextValue {
  value?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
  name?: string;
}

const RadioGroupContext = React.createContext<RadioGroupContextValue>({});

interface RadioGroupProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
  name?: string;
}

const RadioGroup = React.forwardRef<HTMLDivElement, RadioGroupProps>(
  (
    {
      className,
      value: controlledValue,
      defaultValue,
      onValueChange,
      disabled,
      name,
      ...props
    },
    ref
  ) => {
    const [uncontrolledValue, setUncontrolledValue] =
      React.useState(defaultValue);
    const value = controlledValue ?? uncontrolledValue;

    const handleValueChange = React.useCallback(
      (newValue: string) => {
        if (controlledValue === undefined) {
          setUncontrolledValue(newValue);
        }
        onValueChange?.(newValue);
      },
      [controlledValue, onValueChange]
    );

    const contextValue = React.useMemo(
      () => ({ value, onValueChange: handleValueChange, disabled, name }),
      [value, handleValueChange, disabled, name]
    );

    return (
      <RadioGroupContext.Provider value={contextValue}>
        <div
          ref={ref}
          role="radiogroup"
          className={cn("grid gap-2", className)}
          {...props}
        />
      </RadioGroupContext.Provider>
    );
  }
);
RadioGroup.displayName = "RadioGroup";

interface RadioGroupItemProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> {
  value: string;
}

const RadioGroupItem = React.forwardRef<HTMLInputElement, RadioGroupItemProps>(
  ({ className, value: itemValue, disabled: itemDisabled, ...props }, ref) => {
    const context = React.useContext(RadioGroupContext);
    const {
      value: groupValue,
      onValueChange,
      disabled: groupDisabled,
      name,
    } = context;
    const disabled = itemDisabled || groupDisabled;
    const checked = groupValue === itemValue;

    return (
      <div className="relative inline-flex">
        <input
          ref={ref}
          type="radio"
          value={itemValue}
          checked={checked}
          disabled={disabled}
          name={name}
          onChange={() => onValueChange?.(itemValue)}
          className="sr-only"
          {...props}
        />
        <div
          className={cn(
            "h-5 w-5 rounded-full border-4 bg-gray-100 aspect-square cursor-pointer",
            checked ? "border-orange-501" : "border-gray-100",
            disabled && "cursor-not-allowed opacity-50",
            className
          )}
          onClick={() => !disabled && onValueChange?.(itemValue)}
        ></div>
      </div>
    );
  }
);
RadioGroupItem.displayName = "RadioGroupItem";

export { RadioGroup, RadioGroupItem };
